import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../contexts/PageLoaderContext';
import apiService from '../../services/api';
import EmptyState from '../UI/EmptyState';
import SkeletonLoader from '../UI/SkeletonLoader';
import { Chart, registerables } from 'chart.js';
import styles from '../../styles/PlatformReports.module.css';

Chart.register(...registerables);

// Types
interface PlatformMetrics {
  totalUsers: number;
  activeUsers: number;
  totalGroups: number;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  inProgressTasks?: number;
  overdueTasks?: number;
  totalPoints: number;
  totalBadges: number;
  totalBadgesAwarded?: number;
  emailsSentToday: number;
  emailsSentWeek: number;
  emailsSentMonth: number;
  avgResponseTime: number;
  systemUptime: number;
  storageUsed: number;
  storageTotal: number;
}

interface ActivityLog {
  id: string;
  action: string;
  user: string;
  timestamp: string;
  type: 'user' | 'task' | 'email' | 'system' | 'group' | 'badge';
}

interface SystemHealth {
  api_server?: string;
  database?: string;
  email_service?: string;
  storage?: string;
  cdn?: string;
}

interface EmailStats {
  date: string;
  sent: number;
  delivered: number;
  opened: number;
  failed: number;
}

type PeriodType = 'today' | 'week' | 'month' | 'year';

const PlatformReports: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();
  
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('week');
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [emailStats, setEmailStats] = useState<EmailStats[]>([]);
  const [userGrowth, setUserGrowth] = useState<Array<{month: string; total: number; active: number}>>([]);
  const [trends, setTrends] = useState<{
    users?: number;
    tasks?: number;
    emails?: number;
    points?: number;
    groups?: number;
    uptime?: number;
  }>({});
  const [emailPerformance, setEmailPerformance] = useState<{
    delivery_rate?: number;
    open_rate?: number;
    bounce_rate?: number;
  }>({});
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({});

  // Chart refs
  const userChartRef = useRef<HTMLCanvasElement>(null);
  const taskChartRef = useRef<HTMLCanvasElement>(null);
  const emailChartRef = useRef<HTMLCanvasElement>(null);
  const chartInstances = useRef<Chart[]>([]);

  // Load platform data
  const loadPlatformData = useCallback(async () => {
    setLoading(true);
    showLoader('Loading platform reports...');
    
    try {
      const response = await apiService.getPlatformReports({
        period: selectedPeriod
      });
      
      if (response.status === 'success' && response.data) {
        const data = response.data;
        
        // Set metrics
        if (data.metrics) {
          setMetrics({
            totalUsers: data.metrics.total_users || 0,
            activeUsers: data.metrics.active_users || 0,
            totalGroups: data.metrics.total_groups || 0,
            totalTasks: data.metrics.total_tasks || 0,
            completedTasks: data.metrics.completed_tasks || 0,
            pendingTasks: data.metrics.pending_tasks || 0,
            inProgressTasks: data.metrics.in_progress_tasks || 0,
            overdueTasks: data.metrics.overdue_tasks || 0,
            totalPoints: data.metrics.total_points || 0,
            totalBadges: data.metrics.total_badges || 0,
            totalBadgesAwarded: data.metrics.total_badges_awarded || 0,
            emailsSentToday: data.metrics.emails_sent_today || 0,
            emailsSentWeek: data.metrics.emails_sent_week || 0,
            emailsSentMonth: data.metrics.emails_sent_month || 0,
            avgResponseTime: data.metrics.avg_response_time || 0,
            systemUptime: data.metrics.system_uptime || 0,
            storageUsed: data.metrics.storage_used || 0,
            storageTotal: data.metrics.storage_total || 100,
          });
        }
        
        // Set activity logs
        if (data.activity_logs) {
          setActivityLogs(data.activity_logs);
        }
        
        // Set email stats
        if (data.email_stats) {
          setEmailStats(data.email_stats);
        }
        
        // Set user growth data
        if (data.user_growth) {
          setUserGrowth(data.user_growth);
        }
        
        // Set trends
        if (data.trends) {
          setTrends(data.trends);
        }
        
        // Set email performance
        if (data.email_performance) {
          setEmailPerformance(data.email_performance);
        }
        
        // Set system health
        if (data.system_health) {
          setSystemHealth(data.system_health);
        }
      } else {
        notificationService.error(response.message || 'Failed to load platform reports');
      }
    } catch (error: any) {
      console.error('Error loading platform data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load platform reports';
      notificationService.error(errorMessage);
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, [showLoader, hideLoader, notificationService, selectedPeriod]);

  useEffect(() => {
    loadPlatformData();
  }, [selectedPeriod]);

  // Initialize charts
  useEffect(() => {
    if (!loading && metrics && emailStats.length > 0 && userGrowth.length > 0) {
      // Destroy existing charts
      chartInstances.current.forEach(chart => chart.destroy());
      chartInstances.current = [];

      // User Growth Chart - using real data from API
      if (userChartRef.current && userGrowth.length > 0) {
        const ctx = userChartRef.current.getContext('2d');
        if (ctx) {
          const chart = new Chart(ctx, {
            type: 'line',
            data: {
              labels: userGrowth.map(d => d.month),
              datasets: [{
                label: 'Total Users',
                data: userGrowth.map(d => d.total),
                borderColor: '#1a237e',
                backgroundColor: 'rgba(26, 35, 126, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: '#1a237e',
              }, {
                label: 'Active Users',
                data: userGrowth.map(d => d.active),
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: '#10b981',
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: { usePointStyle: true, padding: 15 }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(0,0,0,0.05)' }
                },
                x: {
                  grid: { display: false }
                }
              }
            }
          });
          chartInstances.current.push(chart);
        }
      }

      // Task Distribution Chart - using real data
      if (taskChartRef.current && metrics) {
        const ctx = taskChartRef.current.getContext('2d');
        if (ctx) {
          const chart = new Chart(ctx, {
            type: 'doughnut',
            data: {
              labels: ['Completed', 'In Progress', 'Pending', 'Overdue'],
              datasets: [{
                data: [
                  metrics.completedTasks,
                  metrics.inProgressTasks || 0,
                  metrics.pendingTasks,
                  metrics.overdueTasks || 0
                ],
                backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'],
                borderWidth: 0,
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              cutout: '70%',
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: { usePointStyle: true, padding: 15 }
                }
              }
            }
          });
          chartInstances.current.push(chart);
        }
      }

      // Email Stats Chart
      if (emailChartRef.current) {
        const ctx = emailChartRef.current.getContext('2d');
        if (ctx) {
          const chart = new Chart(ctx, {
            type: 'bar',
            data: {
              labels: emailStats.map(s => s.date),
              datasets: [{
                label: 'Sent',
                data: emailStats.map(s => s.sent),
                backgroundColor: '#1a237e',
                borderRadius: 4,
              }, {
                label: 'Delivered',
                data: emailStats.map(s => s.delivered),
                backgroundColor: '#10b981',
                borderRadius: 4,
              }, {
                label: 'Opened',
                data: emailStats.map(s => s.opened),
                backgroundColor: '#3b82f6',
                borderRadius: 4,
              }]
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: { usePointStyle: true, padding: 15 }
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  grid: { color: 'rgba(0,0,0,0.05)' }
                },
                x: {
                  grid: { display: false }
                }
              }
            }
          });
          chartInstances.current.push(chart);
        }
      }
    }

    return () => {
      chartInstances.current.forEach(chart => chart.destroy());
    };
  }, [loading, metrics, emailStats, userGrowth]);

  // Format timestamp
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Get activity icon
  const getActivityIcon = (type: ActivityLog['type']) => {
    switch (type) {
      case 'user':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        );
      case 'task':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 11l3 3L22 4"></path>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
        );
      case 'email':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
            <polyline points="22,6 12,13 2,6"></polyline>
          </svg>
        );
      case 'group':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        );
      case 'system':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <line x1="8" y1="21" x2="16" y2="21"></line>
            <line x1="12" y1="17" x2="12" y2="21"></line>
          </svg>
        );
      case 'badge':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="7"></circle>
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
          </svg>
        );
      default:
        return null;
    }
  };

  // Get activity color class
  const getActivityColorClass = (type: ActivityLog['type']) => {
    switch (type) {
      case 'user': return styles.activityUser;
      case 'task': return styles.activityTask;
      case 'email': return styles.activityEmail;
      case 'group': return styles.activityGroup;
      case 'system': return styles.activitySystem;
      case 'badge': return styles.activityBadge;
      default: return '';
    }
  };

  // Format number with K/M suffix
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Export platform reports to CSV
  const handleExport = () => {
    if (!metrics) {
      notificationService.error('No data available to export');
      return;
    }

    try {
      // Create CSV content
      const csvRows: string[] = [];
      
      // Header
      csvRows.push('Platform Reports Export');
      csvRows.push(`Period: ${selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}`);
      csvRows.push(`Generated: ${new Date().toLocaleString()}`);
      csvRows.push('');
      
      // Metrics Section
      csvRows.push('METRICS');
      csvRows.push('Metric,Value');
      csvRows.push(`Total Users,${metrics.totalUsers}`);
      csvRows.push(`Active Users,${metrics.activeUsers}`);
      csvRows.push(`Total Groups,${metrics.totalGroups}`);
      csvRows.push(`Total Tasks,${metrics.totalTasks}`);
      csvRows.push(`Completed Tasks,${metrics.completedTasks}`);
      csvRows.push(`Pending Tasks,${metrics.pendingTasks}`);
      csvRows.push(`In Progress Tasks,${metrics.inProgressTasks || 0}`);
      csvRows.push(`Overdue Tasks,${metrics.overdueTasks || 0}`);
      csvRows.push(`Total Points,${metrics.totalPoints}`);
      csvRows.push(`Total Badges,${metrics.totalBadges}`);
      csvRows.push(`Total Badges Awarded,${metrics.totalBadgesAwarded || 0}`);
      csvRows.push(`Emails Sent Today,${metrics.emailsSentToday}`);
      csvRows.push(`Emails Sent This Week,${metrics.emailsSentWeek}`);
      csvRows.push(`Emails Sent This Month,${metrics.emailsSentMonth}`);
      csvRows.push(`System Uptime,${metrics.systemUptime}%`);
      csvRows.push(`Storage Used,${metrics.storageUsed} GB`);
      csvRows.push(`Storage Total,${metrics.storageTotal} GB`);
      csvRows.push('');
      
      // Trends Section
      if (trends) {
        csvRows.push('TRENDS');
        csvRows.push('Trend,Percentage');
        if (trends.users !== undefined) csvRows.push(`User Growth,${trends.users}%`);
        if (trends.tasks !== undefined) csvRows.push(`Task Completion,${trends.tasks}%`);
        if (trends.emails !== undefined) csvRows.push(`Emails Sent,${trends.emails}%`);
        if (trends.points !== undefined) csvRows.push(`Points Earned,${trends.points}%`);
        if (trends.groups !== undefined) csvRows.push(`Groups Created,${trends.groups}%`);
        csvRows.push('');
      }
      
      // Email Performance Section
      if (emailPerformance) {
        csvRows.push('EMAIL PERFORMANCE');
        csvRows.push('Metric,Value');
        csvRows.push(`Delivery Rate,${emailPerformance.delivery_rate || 0}%`);
        csvRows.push(`Open Rate,${emailPerformance.open_rate || 0}%`);
        csvRows.push(`Bounce Rate,${emailPerformance.bounce_rate || 0}%`);
        csvRows.push('');
      }
      
      // User Growth Section
      if (userGrowth.length > 0) {
        csvRows.push('USER GROWTH (Last 6 Months)');
        csvRows.push('Month,Total Users,Active Users');
        userGrowth.forEach(growth => {
          csvRows.push(`${growth.month},${growth.total},${growth.active}`);
        });
        csvRows.push('');
      }
      
      // Task Distribution Section
      if (metrics) {
        csvRows.push('TASK DISTRIBUTION');
        csvRows.push('Status,Count');
        csvRows.push(`Completed,${metrics.completedTasks}`);
        csvRows.push(`In Progress,${metrics.inProgressTasks || 0}`);
        csvRows.push(`Pending,${metrics.pendingTasks}`);
        csvRows.push(`Overdue,${metrics.overdueTasks || 0}`);
        csvRows.push('');
      }
      
      // Activity Logs Section
      if (activityLogs.length > 0) {
        csvRows.push('RECENT ACTIVITY LOGS');
        csvRows.push('Timestamp,Action,User,Type');
        activityLogs.forEach(log => {
          const timestamp = new Date(log.timestamp).toLocaleString();
          csvRows.push(`"${timestamp}","${log.action}","${log.user}","${log.type}"`);
        });
      }
      
      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `platform_reports_${selectedPeriod}_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      notificationService.success('Platform reports exported successfully');
    } catch (error: any) {
      console.error('Export error:', error);
      notificationService.error('Failed to export platform reports. Please try again.');
    }
  };

  return (
    <div className={styles.pageContainer}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>Platform Reports</h1>
          <p className={styles.pageSubtitle}>Real-time analytics and insights</p>
        </div>
        <div className={styles.headerRight}>
          {/* Period Selector */}
          <div className={styles.periodSelector}>
            {(['today', 'week', 'month', 'year'] as PeriodType[]).map(period => (
              <button
                key={period}
                className={`${styles.periodBtn} ${selectedPeriod === period ? styles.periodBtnActive : ''}`}
                onClick={() => setSelectedPeriod(period)}
              >
                {period.charAt(0).toUpperCase() + period.slice(1)}
              </button>
            ))}
          </div>
          <button className={styles.exportBtn} onClick={handleExport} disabled={!metrics || loading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <SkeletonLoader variant="card" count={4} />
        </div>
      ) : !metrics ? (
        <div className={styles.emptyContainer}>
          <EmptyState
            image="/images/notasks.png"
            title="No Data Available"
            description="Platform reports data is not available at the moment."
          />
        </div>
      ) : (
        <>
          {/* Metrics Grid */}
          <div className={styles.metricsGrid}>
            <div className={styles.metricCard}>
              <div className={styles.metricIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{formatNumber(metrics.totalUsers)}</span>
                <span className={styles.metricLabel}>Total Users</span>
              </div>
              <span className={`${styles.metricTrend} ${styles.trendUp}`}>
                {trends.users !== undefined ? `+${trends.users.toFixed(1)}%` : 'N/A'}
              </span>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconGreen}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{formatNumber(metrics.completedTasks)}</span>
                <span className={styles.metricLabel}>Completed Tasks</span>
              </div>
              <span className={`${styles.metricTrend} ${styles.trendUp}`}>
                {trends.tasks !== undefined ? `+${trends.tasks.toFixed(1)}%` : 'N/A'}
              </span>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconBlue}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{formatNumber(metrics.emailsSentWeek)}</span>
                <span className={styles.metricLabel}>Emails This Week</span>
              </div>
              <span className={`${styles.metricTrend} ${styles.trendUp}`}>
                {trends.emails !== undefined ? `+${trends.emails.toFixed(1)}%` : 'N/A'}
              </span>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconOrange}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{formatNumber(metrics.totalPoints)}</span>
                <span className={styles.metricLabel}>Total Points</span>
              </div>
              <span className={`${styles.metricTrend} ${styles.trendUp}`}>
                {trends.points !== undefined ? `+${trends.points.toFixed(1)}%` : 'N/A'}
              </span>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconPurple}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{metrics.totalGroups}</span>
                <span className={styles.metricLabel}>Active Groups</span>
              </div>
              <span className={`${styles.metricTrend} ${styles.trendUp}`}>
                {trends.groups !== undefined ? `+${trends.groups.toFixed(1)}%` : 'N/A'}
              </span>
            </div>

            <div className={styles.metricCard}>
              <div className={`${styles.metricIcon} ${styles.iconCyan}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <div className={styles.metricContent}>
                <span className={styles.metricValue}>{metrics.systemUptime}%</span>
                <span className={styles.metricLabel}>System Uptime</span>
              </div>
              <span className={`${styles.metricTrend} ${styles.trendUp}`}>
                {trends.uptime !== undefined ? `+${trends.uptime.toFixed(2)}%` : 'N/A'}
              </span>
            </div>
          </div>

          {/* Charts Section */}
          <div className={styles.chartsSection}>
            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <h4 className={styles.chartTitle}>User Growth</h4>
                <span className={styles.chartSubtitle}>Last 6 months</span>
              </div>
              <div className={styles.chartContainer}>
                <canvas ref={userChartRef}></canvas>
              </div>
            </div>

            <div className={styles.chartCard}>
              <div className={styles.chartHeader}>
                <h4 className={styles.chartTitle}>Task Distribution</h4>
                <span className={styles.chartSubtitle}>Current status</span>
              </div>
              <div className={styles.chartContainer}>
                <canvas ref={taskChartRef}></canvas>
              </div>
            </div>
          </div>

          {/* Email Analytics Section */}
          <div className={styles.emailSection}>
            <div className={styles.emailChartCard}>
              <div className={styles.chartHeader}>
                <h4 className={styles.chartTitle}>Email Analytics</h4>
                <span className={styles.chartSubtitle}>Daily breakdown this week</span>
              </div>
              <div className={styles.emailChartContainer}>
                <canvas ref={emailChartRef}></canvas>
              </div>
            </div>

            <div className={styles.emailStatsCard}>
              <h4 className={styles.emailStatsTitle}>Email Performance</h4>
              <div className={styles.emailStatsList}>
                <div className={styles.emailStatItem}>
                  <div className={styles.emailStatIcon}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  </div>
                  <div className={styles.emailStatContent}>
                    <span className={styles.emailStatValue}>{metrics.emailsSentToday}</span>
                    <span className={styles.emailStatLabel}>Sent Today</span>
                  </div>
                </div>
                <div className={styles.emailStatItem}>
                  <div className={`${styles.emailStatIcon} ${styles.iconGreen}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <div className={styles.emailStatContent}>
                    <span className={styles.emailStatValue}>
                      {emailPerformance.delivery_rate !== undefined ? `${emailPerformance.delivery_rate.toFixed(1)}%` : 'N/A'}
                    </span>
                    <span className={styles.emailStatLabel}>Delivery Rate</span>
                  </div>
                </div>
                <div className={styles.emailStatItem}>
                  <div className={`${styles.emailStatIcon} ${styles.iconBlue}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </div>
                  <div className={styles.emailStatContent}>
                    <span className={styles.emailStatValue}>
                      {emailPerformance.open_rate !== undefined ? `${emailPerformance.open_rate.toFixed(1)}%` : 'N/A'}
                    </span>
                    <span className={styles.emailStatLabel}>Open Rate</span>
                  </div>
                </div>
                <div className={styles.emailStatItem}>
                  <div className={`${styles.emailStatIcon} ${styles.iconRed}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                  </div>
                  <div className={styles.emailStatContent}>
                    <span className={styles.emailStatValue}>
                      {emailPerformance.bounce_rate !== undefined ? `${emailPerformance.bounce_rate.toFixed(1)}%` : 'N/A'}
                    </span>
                    <span className={styles.emailStatLabel}>Bounce Rate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section: Activity & System Health */}
          <div className={styles.bottomSection}>
            <div className={styles.activityCard}>
              <div className={styles.cardHeader}>
                <h4 className={styles.cardTitle}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                  Recent Activity
                </h4>
              </div>
              <div className={styles.activityList}>
                {activityLogs.map(log => (
                  <div key={log.id} className={styles.activityItem}>
                    <div className={`${styles.activityIcon} ${getActivityColorClass(log.type)}`}>
                      {getActivityIcon(log.type)}
                    </div>
                    <div className={styles.activityContent}>
                      <span className={styles.activityAction}>{log.action}</span>
                      <span className={styles.activityMeta}>{log.user} • {formatTimestamp(log.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.systemCard}>
              <div className={styles.cardHeader}>
                <h4 className={styles.cardTitle}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  System Health
                </h4>
              </div>
              <div className={styles.healthList}>
                <div className={styles.healthItem}>
                  <span className={`${styles.healthDot} ${systemHealth.api_server === 'Operational' ? styles.healthGood : styles.healthWarning}`}></span>
                  <span className={styles.healthLabel}>API Server</span>
                  <span className={styles.healthStatus}>{systemHealth.api_server || 'Unknown'}</span>
                </div>
                <div className={styles.healthItem}>
                  <span className={`${styles.healthDot} ${systemHealth.database === 'Operational' ? styles.healthGood : styles.healthWarning}`}></span>
                  <span className={styles.healthLabel}>Database</span>
                  <span className={styles.healthStatus}>{systemHealth.database || 'Unknown'}</span>
                </div>
                <div className={styles.healthItem}>
                  <span className={`${styles.healthDot} ${systemHealth.email_service === 'Operational' ? styles.healthGood : styles.healthWarning}`}></span>
                  <span className={styles.healthLabel}>Email Service</span>
                  <span className={styles.healthStatus}>{systemHealth.email_service || 'Unknown'}</span>
                </div>
                <div className={styles.healthItem}>
                  <span className={`${styles.healthDot} ${styles.healthWarning}`}></span>
                  <span className={styles.healthLabel}>Storage</span>
                  <span className={styles.healthStatus}>{systemHealth.storage || `${metrics.storageUsed}% Used`}</span>
                </div>
                <div className={styles.healthItem}>
                  <span className={`${styles.healthDot} ${systemHealth.cdn === 'Operational' ? styles.healthGood : styles.healthWarning}`}></span>
                  <span className={styles.healthLabel}>CDN</span>
                  <span className={styles.healthStatus}>{systemHealth.cdn || 'Unknown'}</span>
                </div>
              </div>
              <div className={styles.storageBar}>
                <div className={styles.storageLabel}>
                  <span>Storage Usage</span>
                  <span>{metrics.storageUsed} GB / {metrics.storageTotal} GB</span>
                </div>
                <div className={styles.storageProgress}>
                  <div 
                    className={styles.storageProgressFill}
                    style={{ width: `${(metrics.storageUsed / metrics.storageTotal) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PlatformReports;
